import { useCallback, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import Spinner from "~/common/components/Spinner/Spinner";
import { PageTitle } from "~/common/components/PageTitle/v2/PageTitle";
import { DesktopOnlyMessage } from "~/common/components/DesktopOnlyMessage/DesktopOnlyMessage";
import { HousingFilters } from "~/features/selectionAssistant/housing/components/Filters/HousingFilters";
import { MobileHousingFilters } from "~/features/selectionAssistant/housing/components/Filters/MobileHousingFilters";
import { useResetQualtrics } from "~/features/Analytics/hooks/useResetQualtrics";
import { FormProvider, useForm } from "react-hook-form";
import { type HousingSearchForm, type TechValue } from "~/features/selectionAssistant/housing/types/housingSAFormTypes";
import { NumberSystem, TemperatureUnit } from "~/features/selectionAssistant/housing/types/types";
import { HOUSING_SA_ON_CLICK_UPDATE_CODES } from "~/features/selectionAssistant/housing/constants";
import { initialiseHSAPage, runHSASearch } from "~/features/selectionAssistant/housing/redux/asyncActions";
import { useOnMount } from "~/common/hooks/useOnMount";
import {
  selectIsInFlight,
  selectIsInitialised,
  selectTotalNumberOfResults,
} from "~/features/selectionAssistant/housing/redux/selectors";
import { useIsMinDesktopSize, useIsMinTabletSize } from "~/common/hooks/useMediaQuery";
import { selectBeSortCode } from "~/features/search/redux/searchSelectors";
import { type Diff, diff } from "deep-diff";
import { HSASearchResults } from "~/features/selectionAssistant/housing/components/HousingTabularView/HSASearchResults";
import { useTranslation } from "react-i18next";
import { ShowProductsBtn } from "~/features/selectionAssistant/bearing/components/ShowProductsBtn";
import { ButtonsRow } from "~/pages/BearingSelectionAssistantPage/BearingSelectionAssistantPage";

import { Box, styled } from "~/common/mui";
import { ResetAllLinkBtn } from "~/features/selectionAssistant/bearing/components/Buttons/ResetAllBtn";
import { loadCartAmounts } from "~/features/addToCart/redux/cartAmounts/actions";
import { generateUrlFromHousingFilterValues } from "~/features/search/utils/historyManipulation";
import { SearchReason } from "~/features/selectionAssistant/housing/redux/types";
import { PurchasePlaceProvider, PurchaseTypeProvider } from "~/app/analytics/ga/recommendations/RecommendationContexts";
import {
  HousingResetContext,
  type HousingSearchFormKey,
} from "~/features/selectionAssistant/housing/context/HousingResetContext";

export {
  type HousingSearchFormKey,
  useHSAResetAction,
} from "~/features/selectionAssistant/housing/context/HousingResetContext";

const SelectionAssistantRoot = styled(Box)({
  position: "relative",
  zIndex: 1,
});

export const INITIAL_HOUSING_SA_FORM_VALUES: HousingSearchForm = {
  housinginclbrg: "",
  bearing_type: [],
  bearing_design: [],
  housing_type: "",
  number_of_bolts: [],
  housing_shapes: [],
  housing_design: [],
  sealing_type: [],

  housingload: [],
  housinglubrication: [],
  housingcoating: [],
  housingmisalignment: [],

  housinginclhsg: "",
  bearing_mounting_type: [],
  housing_material: [],

  dimensionUnit: NumberSystem.Metric,
  housingdinner: { min: "", max: "" },
  housingdouter: { min: "", max: "" },
  housinghheight: { min: "", max: "" },
  housingllength: { min: "", max: "" },
  housingbwidth: { min: "", max: "" },
  housinghseat: { min: "", max: "" },
  housingmdistance: { min: "", max: "" },
  housingndistance: { min: "", max: "" },

  temperatureUnit: TemperatureUnit.Celsius,
  housingtempmin: { min: "", max: "" },

  page: 0,
  sortCode: "",
};

function prepareResetData(currentForm: HousingSearchForm, ...keys: HousingSearchFormKey[]): Partial<HousingSearchForm> {
  if (!keys.length) {
    return { ...INITIAL_HOUSING_SA_FORM_VALUES };
  } else {
    const data: Partial<HousingSearchForm> = { ...currentForm };
    keys.forEach((key) => {
      (data as Record<string, HousingSearchForm[keyof HousingSearchForm]>)[key] = INITIAL_HOUSING_SA_FORM_VALUES[key];
    });
    return data;
  }
}

function determineSearchReason(differences: Array<Diff<HousingSearchForm>> | undefined) {
  const changedFields = differences?.map((diff) => diff.path![0] as string) ?? [];

  if (changedFields.some((code) => code === "sortCode")) {
    return SearchReason.SORTING;
  }
  if (changedFields.length === 1 && changedFields[0] === "page") {
    return SearchReason.PAGINATION;
  }
  return SearchReason.FILTER;
}

export const HousingSelectionAssistantContainer = () => {
  const isMinTablet = useIsMinTabletSize();

  if (!isMinTablet) {
    return <HSATooSmall />;
  }

  return <HousingSelectionAssistant />;
};

export const HSATooSmall = () => {
  const { t } = useTranslation();

  return (
    <DesktopOnlyMessage title={t("page.housingSelectionAssistant.title")}>
      {t("page.sa.housing.tooSmall")}
    </DesktopOnlyMessage>
  );
};

const HousingSelectionAssistant = () => {
  const { t } = useTranslation();
  const isDesktop = useIsMinDesktopSize();

  const dispatch = useDispatch();

  const isInitialized = useSelector(selectIsInitialised);
  const isInFlight = useSelector(selectIsInFlight);
  const totalNumberOfResults = useSelector(selectTotalNumberOfResults);

  const initialFormData = { ...INITIAL_HOUSING_SA_FORM_VALUES };

  const formProps = useForm<HousingSearchForm>({
    defaultValues: initialFormData,
  });

  const beSortCode = useSelector(selectBeSortCode) ?? "";
  useEffect(() => {
    formProps.setValue("sortCode", beSortCode);
    if (previousFormData.current.sortCode === "") {
      previousFormData.current = formProps.getValues();
    } else if (isInitialized) {
      submitHandler();
    }
  }, [beSortCode]);

  const previousFormData = useRef(initialFormData);
  const submitHandler = useMemo(
    () =>
      formProps.handleSubmit((data) => {
        const differences = diff(previousFormData.current, data);
        const searchReason = determineSearchReason(differences);
        previousFormData.current = data;
        const sortCodeDiff = differences?.find((d) => (d.path?.[0] as string) === "sortCode") as Diff<string> | null;
        if (sortCodeDiff && sortCodeDiff.kind === "E" && sortCodeDiff.lhs === "") {
          return;
        }
        window.history.replaceState({}, "", generateUrlFromHousingFilterValues(data));
        dispatch(runHSASearch(data, searchReason));
      }),
    [],
  );

  const resetForm = useCallback((forceSubmit?: boolean, ...keys: HousingSearchFormKey[]) => {
    formProps.reset(prepareResetData(formProps.getValues(), ...keys));

    if (forceSubmit) {
      submitHandler();
    }
  }, []);

  useResetQualtrics([isInFlight]);

  useOnMount(() => {
    dispatch(initialiseHSAPage(formProps.getValues()));
    dispatch(loadCartAmounts());
  });

  const dependencyValues = formProps.watch(HOUSING_SA_ON_CLICK_UPDATE_CODES as HousingSearchFormKey[]);
  const submitDependencyString = createDependencyString(dependencyValues);
  useEffect(() => {
    if (isInitialized) {
      formProps.setValue("page", 0);
      submitHandler();
    }
  }, [submitDependencyString]);

  const transformParamsObject = (input: object) => {
    type AccumulatorValue = string | string[] | number | Partial<TechValue>;
    return Object.entries(input).reduce(
      (result, [key, value]) => {
        const [prefix, subKey] = key.split("-value");

        if (!result[prefix]) {
          result[prefix] = {};
        }

        if (subKey === "min" || subKey === "max") {
          const techValue = result[prefix] as Partial<TechValue>;
          if (!techValue.min) {
            techValue.min = "";
          }
          if (!techValue.max) {
            techValue.max = "";
          }
          techValue[subKey] = value as string;
        } else {
          result[prefix] = value;
        }

        return result;
      },
      {} as Record<string, AccumulatorValue>,
    ) as Record<keyof HousingSearchForm, string | string[] | number | TechValue>;
  };

  const applyFiltersBasedOnUrl = () => {
    const urlParams = new URLSearchParams(window.location.search.toString());
    const entries = urlParams.keys();
    const params: Record<string, string | string[]> = {};

    for (const key of entries) {
      const entryValue = urlParams.getAll(key);
      if (entryValue.length === 1 && !Array.isArray(formProps.getValues(key as keyof HousingSearchForm))) {
        params[key] = entryValue[0];
      } else {
        params[key] = entryValue;
      }
    }

    const modifiedParams = transformParamsObject(params);

    for (const [key, value] of Object.entries(modifiedParams)) {
      formProps.setValue(key as keyof HousingSearchForm, value as HousingSearchForm[keyof HousingSearchForm]);
    }
  };

  useEffect(() => {
    if (isInitialized) {
      applyFiltersBasedOnUrl();
    }
  }, [isInitialized]);

  return (
    <PurchasePlaceProvider value={"HSA"}>
      <PurchaseTypeProvider value={"hsa"}>
        <FormProvider {...formProps}>
          <HousingResetContext.Provider value={resetForm}>
            <form onSubmit={submitHandler}>
              <SelectionAssistantRoot>
                <PageTitle>{t("page.housingSelectionAssistant.title")}</PageTitle>

                {isInitialized && (
                  <ButtonsRow>
                    <ShowProductsBtn resultAmount={totalNumberOfResults} />
                    <ResetAllLinkBtn onClick={() => resetForm()} />
                  </ButtonsRow>
                )}

                <div id="filters" hidden={!isInitialized}>
                  {isDesktop && <HousingFilters />}
                  {!isDesktop && <MobileHousingFilters resetData={resetForm} />}
                </div>

                {!isInitialized && <Spinner />}

                {isInitialized && <HSASearchResults submitHandler={submitHandler} />}
              </SelectionAssistantRoot>
            </form>
          </HousingResetContext.Provider>
        </FormProvider>
      </PurchaseTypeProvider>
    </PurchasePlaceProvider>
  );
};

export function createHousingSelectionAssistant() {
  return <HousingSelectionAssistantContainer />;
}

type FormFieldType = string | number | string[] | TechValue;
const createDependencyString = (formFieldValues: readonly FormFieldType[]): string => {
  return formFieldValues.map((el: FormFieldType) => (Array.isArray(el) ? el.join("|") : el)).join("-");
};
